import {
  Transaction,
} from "@/types/pos/transaction";
import {
  SalesReport,
} from "@/types/pos/sales-history"
import React, { ReactNode } from "react";

interface ReceiptProps {
  salesReport: SalesReport;
}
import { capitalizeFirstLetter, displayDate, formatNIC, formatToPKR, hasFullAccessToMemberNIC } from "@/lib/helper";
import { InvoiceDetails } from "@/types/invoice";
import { SaleStatus } from "@/types/enums";

const Receipt: React.FC<ReceiptProps> = ({ salesReport }) => {
  const isRefund = salesReport.transaction_type == SaleStatus.Refund;

  return (
    <div className="p-6 border border-gray-300 rounded-lg shadow-md max-w-sm mx-auto">
      <h2 className="text-xl font-bold text-center mb-4">
        {isRefund ? "Refund Receipt" : "Sale Receipt"}
      </h2>
      <div className="flex flex-col  gap-2  justify-center items-center">
        <p>Receipt Number: {salesReport.receipt_number}</p>
        <p>Transaction Date: {salesReport?.transaction_date as ReactNode}</p>
      </div>

      <h4 className="font-semibold mt-2 mb-2">Member Details</h4>
      <div className="flex justify-center gap-1 flex-col items-start">
        <p>Name: {capitalizeFirstLetter(salesReport.member_name)}</p>
        <p>Email: {capitalizeFirstLetter(salesReport.member_email)}</p>
        <p>Address: {capitalizeFirstLetter(salesReport.member_address)}</p>
        <p>Gender: {capitalizeFirstLetter(salesReport.member_gender)}</p>
      </div>

      <hr className="my-4" />

      <h4 className="font-semibold">Items</h4>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-300 px-2 py-1">Name</th>
            <th className="border border-gray-300 px-2 py-1">Quantity</th>
            <th className="border border-gray-300 px-2 py-1">Price</th>
            <th className="border border-gray-300 px-2 py-1">Total</th>
          </tr>
        </thead>
        <tbody>
          {salesReport.items?.map((item) => (
            <tr key={item.item_id}>
              <td className="border border-gray-300 px-2 py-1">
                {capitalizeFirstLetter(item.description ?? "")}
              </td>
              <td className="border border-gray-300 px-2 py-1">
                {item.quantity}
              </td>
              <td className="border border-gray-300 px-2 py-1">
                {item.price?.toFixed(2)}
              </td>
              <td className="border border-gray-300 px-2 py-1">
                {item.total?.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr className="my-4" />
      <h4 className="font-semibold pb-2">Summary</h4>
      <div className="flex flex-col justify-center items-start gap-2">
        <p>
          Subtotal:{" "}
          <span className="font-bold">{salesReport.sub_total.toFixed(2)}</span>
        </p>
        <p>
          Discount:{" "}
          <span className="font-bold">
            {salesReport.discount.toFixed(2)}
          </span>
        </p>
        <p>
          Tax Amount:{" "}
          <span className="font-bold">{salesReport.tax_amount.toFixed(2)}</span>
        </p>
        {/* <h4 className="font-bold"> */}
        <h4>
          Total:{" "}
          <span className="text-lg font-bold">
            {salesReport.total.toFixed(2)}
          </span>
        </h4>

        <p>Notes: {capitalizeFirstLetter(salesReport.notes)}</p>
      </div>
    </div>
  );
};

export default Receipt;

export function ReceiptExport(salesReport: SalesReport | Transaction) {
  const { user } = JSON.parse(localStorage.getItem("userInfo") as string);
  const isOwner = hasFullAccessToMemberNIC();
  return user && salesReport && `<!DOCTYPE html>
<html lang="en">

<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Invoice</title>
    <style>
    @page {
      margin: 0 0 40px 0;
      size: auto;
    }
            
    @page :first {
      margin-top: 0;
    }
            
    @page :left {
      margin-left: 0;
    }
            
    @page :right {
      margin-right: 0;
    }
                    
    @media print {
      @page {
        margin: 0 0 40px 0;
        size: auto;
      }    
    }
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px 20px 60px 20px;
        }

        .container {
            margin: 0 auto;
            background-color: #ffffff;
            padding: 20px;
        }

        .header {
            text-align: center;
            margin-bottom: 20px;
        }

        .header h2 {
            margin: 0;
            font-size: 24px;
            font-weight: bold;
        }

        .header p {
            margin: 4px 0;
            color: #555;
        }

        .details {
            margin-bottom: 20px;
            color: #333;
        }

        .details p {
            margin: 4px 0;
            font-size: 14px;
        }

        .details span {
            font-weight: bold;
        }

        .price {
            text-align: right !important;
        }



        .table-container {
            width: 100%;
            border-collapse: collapse;
        }

        .table-container th,
        .table-container td {
            border-bottom: 1px dashed #000;
            padding: 5px 0px;
            text-align: left;
        }

        .table-container th {
            font-weight: bold;
            color: #333;
            padding: 5px 0px;
        }



        .summary {
            color: #333;
        }

        .summary p {
            margin: 4px 0;
            font-size: 14px;
        }

        .summary span {
            font-weight: bold;
        }
    </style>
</head>

<body>
    <div class="container">
        <!-- Header Section -->
        <div class="header">
            <h2>${user?.org_name}</h2>
        </div>

        <!-- Receipt Details -->
        <div class="details">
            <p><span>SRB Invoice No:</span> ${salesReport.srb_invoice_id ?? ""}</p>
            <p><span>Transaction ID:</span> ${salesReport.receipt_number ?? ""}</p>
            <p><span>Date:</span> ${displayDate(salesReport.created_at)}</p>
            <p><span>Name:</span> <span style="font-weight:normal; text-transform:uppercase;">${salesReport.member_name}</span></p>
            ${salesReport.member_nic && isOwner ? `<p><span>${salesReport.is_business ? "NTN" : "CNIC"}:</span> <span style="font-weight:normal; text-transform:uppercase;">${salesReport.is_business ? salesReport.member_nic : formatNIC(salesReport.member_nic)}</span></p>` : ""}
            <p><span>Transaction Type:</span> <span style="font-weight:normal; text-transform:uppercase;">${salesReport.transaction_type == "Sale" ? "Receipt" : salesReport.transaction_type}</span></p>
            <p><span>Status:</span> <span style="text-transform:uppercase;">${salesReport.status}</span></p>
        </div>

        <!-- Invoice Table -->
        <table class="table-container">
            <tr>
                <th>Item</th>
                <th>Qty</th>
                <th class="price">Price</th>
            </tr>
            
            ${salesReport?.items
      ?.filter(item => item)  // Filter out any falsy values
      .map((item) =>
        `<tr>
                  <td style="text-transform: capitalize;">${item.description}</td>
                  <td>${item.quantity}</td>
                  <td class="price">${formatToPKR(item.sub_total)}</td>
                </tr>`
      ).join('')}
        </table>

        <!-- Summary Section -->
        <table class="table-container">
            <tr>
                <td class="price">Sub Total:</td>
                <td class="price">${formatToPKR(salesReport.sub_total as number)}</td>
            </tr>
            <tr>
                <td class="price">Discount:</td>
                <td class="price">${formatToPKR(salesReport.discount as number)}</td>
            </tr>
            <tr>
                <td class="price">Sindh Sales Tax:</td>
                <td class="price">${formatToPKR(salesReport.tax_amount as number)}</td>
            </tr>
            <tr>
                <td class="price">Grand Total:</td>
                <td class="price">${formatToPKR((salesReport.transaction_type == SaleStatus.Refund ? -Number(salesReport.total) : salesReport.total) as number)}</td>
            </tr>
            ${salesReport?.payments
      ?.filter(payment => payment && payment.payment_method)  // Filter out any falsy values
      .map((payment) =>
        `<tr>  
                    <td class="price" style="text-transform:capitalize">
                        ${payment.payment_method}
                    </td>
                    <td class="price">
                        ${formatToPKR(payment.payment_method === "Cash" ? (payment.amount + salesReport.change_amount) : payment.amount)}
                    </td>
                </tr>`
      ).join('')}

                ${Number(salesReport.change_amount) > 0 ? `<td class="price" style="border:none;">Change Given:</td>
                <td class="price" style="border:none;">${formatToPKR((salesReport.change_amount) as number)}</td>` : ""}
            </tr>
        </table>
    </div>
</body>

</html>`;
}
export function InvoiceExport(invoice: InvoiceDetails) {
  const { user } = JSON.parse(localStorage.getItem("userInfo") as string);
  const isOwner = hasFullAccessToMemberNIC();

  return user && invoice && `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>

     @page {
      margin: 0 0 40px 0;
      size: auto;
    }
            
    @page :first {
      margin-top: 0;
    }
            
    @page :left {
      margin-left: 0;
    }
            
    @page :right {
      margin-right: 0;
    }
            
    body {
      margin: 0;
      padding: 20px;
      font-family: Arial, sans-serif;
    }
            
    @media print {
      @page {
        margin: 0 0 40px 0;
        size: auto;
      }   
    }    
      body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 20px 20px 60px 20px;
      }
      
      .card {
        background-color: white;
        margin: 0 auto;
      }
      .card-header {
        padding: 20px;
        margin:auto 20px;
        border-bottom: 1px solid #e0e0e0;
      }
      .card-header h2 {
        margin: 0;
        font-size: 1.25rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
      }
      .action-buttons {
        display: flex;
        gap: 10px;
        margin-top: 15px;
      }
      .btn {
        padding: 8px 12px;
        border: 1px solid #ccc;
        background-color: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.875rem;
      }
      .btn-delete {
        color: #e11d48;
      }
      .tabs {
        display: flex;
        border-bottom: 1px solid #e0e0e0;
        margin-top: 15px;
      }
      .tab {
        padding: 10px 15px;
        cursor: pointer;
      }
      .tab.active {
        border-bottom: 2px solid #2563eb;
        color: #2563eb;
      }
      .card-content {
        padding: 20px;
      }
      .amount {
        margin-bottom: 20px;
      }
      .amount-label {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 0.875rem;
        color: #666;
      }
      .badge {
        background-color: #fef3c7;
        color: #d97706;
        padding: 2px 8px;
        border-radius: 9999px;
        font-size: 0.75rem;
      }
      .amount-value {
        font-size: 2rem;
        font-weight: bold;
        color: #e11d48;
        margin-top: 5px;
      }
      .details-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 20px;
      }
      .detail-item h4 {
        margin: 0;
        font-size: 0.875rem;
        color: #666;
      }
      .detail-item p {
        margin: 5px 0 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      th,
      td {
        text-align: left;
        padding: 10px;
        border-bottom: 1px solid #e0e0e0;
      }
      th {
        font-weight: normal;
        color: #666;
      }
      .text-right {
        text-align: right;
      }
      .totals {
        padding-top: 20px;
        width: 40%;
        margin-left: auto;
      }
      .total-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }
      .total-row.bold {
        font-weight: bold;
      }
      .total-row.muted {
        color: #666;
      }
      @media (max-width: 900px) {
        .details-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .action-buttons {
          flex-wrap: wrap;
        }
        .btn {
          flex: 1 0 calc(50% - 5px);
        }
      }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="card-header">
        <h2 style="width: fit-content; margin: auto">
          ${user.org_name}
        </h2>
        <!--<p style="width: fit-content; margin: auto">
          500 yong Street north york
        </p>-->
        <!--<p style="width: fit-content; margin: auto">info@atmosphere.com</p>-->
      </div>

      <div class="card-content">
        <div class="details-grid">
          <div class="detail-item">
            <h4>Bill To</h4>
            <p style="text-transform: capitalize;">${invoice.member_name}</p>
          </div>
          <div class="detail-item">
            <h4>Email</h4>
            <p>${invoice.member_email}</p>
          </div>
          ${invoice.member_nic && isOwner ? `<div class="detail-item">
            <h4>${invoice.is_business ? "NTN" : "CNIC"}</h4>
            <p>${invoice.is_business ? invoice.member_nic : formatNIC(invoice.member_nic)}</p>
          </div>` : ""}
          <div class="detail-item">
            <h4>Invoice Number</h4>
            <p>${invoice.invoice_number ?? ""}</p>
          </div>
          

          <div class="detail-item">
            <h4>Invoice Date</h4>
            <p>${displayDate(invoice.created_at)}</p>
          </div>
          
        </div>
        <table>
          <thead >
            <tr>
              <th style="text-wrap:nowrap;">Product</th>
              <th style="text-wrap:nowrap;">Name</th>
              <th style="text-wrap:nowrap;">Book Date</th>
              <th style="text-wrap:nowrap;" class="text-right">Qty</th>
              <th style="text-wrap:nowrap;" class="text-right">Unit Price</th>
              <th style="text-wrap:nowrap;" class="text-right">Net Amount</th>
              <th style="text-wrap:nowrap;" class="text-right">Discount</th>
              <th style="text-wrap:nowrap;" class="text-right">Tax Rate</th>
              <th style="text-wrap:nowrap;" class="text-right">Total</th>
            </tr>
          </thead>
          <tbody >
          ${invoice.items.map(membership => membership ?
    `<tr>
              <td style="text-wrap:nowrap;">${membership.item_type}</td>
              <td style="text-wrap:nowrap; text-transform: capitalize;">${membership.description}</td>
              <td style="text-wrap:nowrap;">${displayDate(membership.booking_date)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${membership.quantity}</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.price)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.sub_total)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.discount)}</td>
              <td style="text-wrap:nowrap;" class="text-right">${membership.tax_rate?.toFixed(2)}%</td>
              <td style="text-wrap:nowrap;" class="text-right">${formatToPKR(membership.total)}</td>
            </tr>`: ""
  ).join('')}  
          
          </tbody>
        </table>
        <div class="totals">
          <div class="total-row bold">
            <span>Subtotal</span>
            <span>${formatToPKR(invoice.sub_total)}</span>
          </div>
          <div class="total-row bold">
            <span>Total Discount </span>
            <span>${formatToPKR(invoice.discount)}</span>
          </div>
          <div class="total-row bold">
            <span>Tax Amount </span>
            <span>${formatToPKR(invoice.tax_amount)}</span>
          </div>
          <div class="total-row bold">
            <span>Total Amount </span>
            <span>${formatToPKR(invoice.total)}</span>
          </div>
        </div>
      </div>
    </div>
  </body>
</html>
`;
}